import { CustomParagraph, TruncateWithTooltip } from '../../../components';
import { Avatar, Button, Flex } from 'antd';
import { FileDownload } from '../../../assets/images';
import React, { useMemo } from 'react';
import { getInitials } from '../../../common/helper';
import CommentAudienceIcons from './COC_Privacy_Comments/CommentAudienceIcons';
import { audienceTeam } from '@/apis/types';
import styles from './CoCReplies.module.css';

interface Attachment {
  file: string;
  file_name: string;
}

export interface Reply {
  id?: string | number;
  author_name: string;
  content: string;
  timestamp: string;
  attachments?: Attachment[];
  audience_type?: string;
  audience_teams?: audienceTeam[];
  author_badge_color?: string;
}

interface Custody {
  id?: string | number;
  replies?: Reply[];
  hidden_replies_count?: number;
}

interface FormattedTime {
  full: string;
  relative: string;
}

interface CoCRepliesProps {
  custody: Custody;
  downloadFile: (file: string, fileName: string) => void;
  getFormattedTime: (timestamp: string) => FormattedTime;
  showFull: boolean;
  setShowFull: React.Dispatch<React.SetStateAction<boolean>>;
  truncateByLength?: (text: string, maxLength: number) => string;
  setOpenReplyCommentInput?: (id: string | number | undefined) => void;
}

/**
 * CoCReplies Component
 *
 * Displays chain of custody replies with author avatars, timestamps,
 * content, and file attachments. Supports color-coded avatars per user
 * and toggleable timestamp formats (relative/full).
 */
const CoCReplies = ({
  custody,
  downloadFile,
  getFormattedTime,
  showFull,
  setShowFull,
  setOpenReplyCommentInput,
}: CoCRepliesProps) => {
  const userColorMap = useMemo(() => {
    const map: Record<string, string> = {};

    custody?.replies?.forEach((reply) => {
      const user = reply?.author_name;

      if (user && !map[user]) {
        map[user] = reply?.author_badge_color || '#ccc';
      }
    });

    return map;
  }, [custody?.replies]);

  return (
    <>
      <Flex vertical gap={12}>
        {custody?.replies?.map((reply) => (
          <div
            id={reply?.id != null ? `coc-${reply.id}` : undefined}
            data-coc-reply-anchor={reply?.id != null ? 'true' : undefined}
            onClick={() => setOpenReplyCommentInput?.(custody?.id)}
            className='reply-option w-full'
            key={reply?.id ?? `${reply?.author_name}-${reply?.timestamp}`}
          >
            <Avatar
              size={36}
              className='reply-avatar'
              style={{
                backgroundColor:
                  userColorMap[reply?.author_name] || 'var(--gray-600)',
              }}
            >
              {getInitials(reply?.author_name)}
            </Avatar>
            <div className={`mention-text ${styles.mentionText}`}>
              <Flex align='center' justify='space-between'>
                <div className='reply-name'>{reply?.author_name}</div>
                <Flex align='center' gap={8}>
                  <CommentAudienceIcons
                    audienceType={reply?.audience_type ?? ''}
                    teams={reply?.audience_teams}
                  />

                  <span
                    onClick={(e) => {
                      setShowFull((prev) => !prev);
                      e.stopPropagation();
                    }}
                    className='cursor-pointer'
                  >
                    <CustomParagraph
                      text={
                        showFull
                          ? getFormattedTime(reply?.timestamp)?.full
                          : getFormattedTime(reply?.timestamp)?.relative
                      }
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                      style={{ whiteSpace: 'nowrap' }}
                    />
                  </span>
                </Flex>
              </Flex>
              <Flex vertical gap={4}>
                <div
                  className='reply-message'
                  dangerouslySetInnerHTML={{ __html: reply?.content }}
                ></div>
                <Flex wrap>
                  {reply?.attachments?.map((item) => (
                    <Flex vertical key={`${item?.file}-${item?.file_name}`}>
                      <Button
                        type='default'
                        className='zl-btn'
                        onClick={() =>
                          downloadFile(item?.file, item?.file_name)
                        }
                      >
                        <img
                          src={FileDownload}
                          width={16}
                          alt=''
                          className='zl-invert-on-dark'
                        />
                      </Button>

                      <TruncateWithTooltip
                        text={String(item?.file_name ?? '')}
                        max={6}
                        render={(t) => (
                          <CustomParagraph
                            text={t}
                            fontSize='10px'
                            fontWeight={400}
                            lineHeight='16px'
                            color='var(--text-secondary)'
                            marginBottom='0'
                          />
                        )}
                      />
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            </div>
          </div>
        ))}
      </Flex>
    </>
  );
};

export default CoCReplies;
